<template>
  <div class="notification-actions">
    <!-- Individual notification actions -->
    <div class="flex items-center gap-2">
      <UButton
        size="xs"
        variant="ghost"
        :icon="isRead ? 'i-heroicons-envelope' : 'i-heroicons-envelope-open'"
        @click="handleToggleRead"
        :disabled="loading"
        :title="isRead ? $t('notifications.mark_unread') : $t('notifications.mark_read')"
      />
      
      <UButton
        v-if="notification.day && showViewButton"
        size="xs"
        variant="ghost"
        icon="i-heroicons-arrow-top-right-on-square"
        @click="handleView"
        :title="$t('notifications.view_details')"
      />
      
      <UButton
        v-if="showDeleteButton"
        size="xs"
        variant="ghost"
        icon="i-heroicons-trash"
        color="red"
        @click="handleDelete"
        :disabled="loading"
        :title="$t('notifications.delete')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { NotificationInfo } from '~/composables/notification'
import { useNotifications } from '~/composables/notification'

interface Props {
  notification?: NotificationInfo
  showViewButton?: boolean
  showDeleteButton?: boolean
  isRead?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showViewButton: true,
  showDeleteButton: false,
  isRead: false
})

const emit = defineEmits<{
  'action-completed': [action: string, success: boolean]
  'view-clicked': [notification: NotificationInfo]
  'read-status-changed': [notificationId: number, isRead: boolean]
}>()

const router = useRouter()
const { $i18n } = useNuxtApp()
const { markAsRead } = useNotifications()

// Local state
const loading = ref(false)

async function handleToggleRead() {
  if (!props.notification) return
  
  loading.value = true
  try {
    const newReadStatus = !props.isRead
    await markAsRead(props.notification.id, newReadStatus)
    
    emit('read-status-changed', props.notification.id, newReadStatus)
    emit('action-completed', 'toggle-read', true)
  } catch (error) {
    console.error('Failed to toggle read status:', error)
    emit('action-completed', 'toggle-read', false)
  } finally {
    loading.value = false
  }
}

async function handleDelete() {
  if (!props.notification) return
  
  loading.value = true
  try {
    // Note: This feature would require a new API endpoint for deleting individual notifications
    // Currently not implemented in the backend
    useToast().add({
      title: $i18n.t('notifications.feature_not_available'),
      description: $i18n.t('notifications.delete_notification_not_implemented'),
      color: 'yellow'
    })
    emit('action-completed', 'delete', false)
  } catch (error) {
    console.error('Failed to delete notification:', error)
    emit('action-completed', 'delete', false)
  } finally {
    loading.value = false
  }
}

function handleView() {
  if (!props.notification) return
  
  emit('view-clicked', props.notification)
  
  // Auto-mark as read when viewing
  if (!props.isRead) {
    handleToggleRead()
  }
  
  // Navigate based on notification type
  switch (props.notification.type) {
    case 'TimeChanged':
      if (props.notification.day && props.notification.user_id) {
        router.push({
          name: 'dashboard-daily_shifts',
          query: {
            day_id: props.notification.day,
            user_id: props.notification.user_id
          }
        })
      }
      break
    case 'TimeApproved':
    case 'TimeUnapproved':
    case 'CommentChanged':
      if (props.notification.day) {
        router.push({
          name: 'dashboard-my_shifts',
          query: {
            day_id: props.notification.day
          }
        })
      }
      break
  }
}
</script>

<style scoped>
.notification-actions {
  display: flex;
  align-items: center;
}
</style>